/**
 * ProfilePage - Gestion du profil client
 * Informations personnelles + Adresses + Sécurité
 */

import { useState, useEffect } from 'react';
import { useTitrePage } from '../../hooks/useTitrePage';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, CheckCircle, AlertCircle } from 'lucide-react';
import { useCustomer } from '../../contexts/CustomerContext';
import { useTenant } from '../../contexts/TenantContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import MesAbonnements from '../../components/ecommerce/MesAbonnements';
import { ecommerceApi } from '../../api/ecommerce.api';
import type { Customer, CustomerAddress } from '../../types/ecommerce.types';

export default function ProfilePage() {
  useTitrePage('Mon profil');
  const navigate = useNavigate();
  const { customer: authCustomer } = useCustomer();
  const { tenant } = useTenant();
  const tenantId = localStorage.getItem('customer_tenant_id') || tenant?.id || '';
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    loading: pushLoading,
    error: pushError,
    subscribe: pushSubscribe,
    unsubscribe: pushUnsubscribe,
    sendTestNotification,
  } = usePushNotifications(tenantId, authCustomer?.id);
  const [testSent, setTestSent] = useState(false);

  const [customer, setCustomerState] = useState<Customer | null>(null);
  const [addresses, setAddresses] = useState<CustomerAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // États formulaires
  const [editingInfo, setEditingInfo] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });

  const [addressForm, setAddressForm] = useState({
    street: '',
    city: '',
    postal_code: '',
    country: 'BE',
    type: 'delivery' as const,
  });

  useEffect(() => {
    if (!authCustomer) {
      navigate('/shop/login');
      return;
    }

    loadProfile();
  }, [authCustomer]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [profileData, addressesData] = await Promise.all([
        ecommerceApi.auth.getProfile(),
        ecommerceApi.addresses.getAddresses(),
      ]);
      setCustomerState(profileData);
      setAddresses(addressesData);
      setFormData({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        phone: profileData.phone || '',
      });
    } catch (err) {
      console.error('Erreur chargement profil:', err);
      setError('Impossible de charger le profil');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInfo = async () => {
    if (!authCustomer) return;

    try {
      setSaving(true);
      setError(null);
      await ecommerceApi.auth.updateProfile(authCustomer.id, formData);

      setSuccess('Informations mises à jour avec succès');
      setEditingInfo(false);
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAddress = async () => {
    try {
      setSaving(true);
      setError(null);
      await ecommerceApi.addresses.createAddress(addressForm);
      setSuccess('Adresse ajoutée avec succès');
      setAddressForm({ street: '', city: '', postal_code: '', country: 'BE', type: 'delivery' });
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'adresse');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette adresse ?')) return;

    try {
      setSaving(true);
      setError(null);
      await ecommerceApi.addresses.deleteAddress(addressId);
      setSuccess('Adresse supprimée avec succès');
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 py-6 md:py-8 px-3 md:px-4 pb-24 md:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => navigate('/shop')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour à la boutique
          </button>

          <h1 className="text-4xl font-bold text-gray-900">Mon profil</h1>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 mb-6">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-green-50 border-2 border-green-500 rounded-xl p-4 mb-6">
            <p className="text-green-700 font-medium">✓ {success}</p>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4" />
            <p className="text-gray-600 text-xl">Chargement...</p>
          </div>
        ) : (
          <>
            {/* Informations Personnelles */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Informations personnelles</h2>
                {!editingInfo && (
                  <button
                    onClick={() => setEditingInfo(true)}
                    className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
                  >
                    ✏️ Modifier
                  </button>
                )}
              </div>

              {editingInfo ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prénom</label>
                      <input
                        type="text"
                        value={formData.first_name}
                        onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nom</label>
                      <input
                        type="text"
                        value={formData.last_name}
                        onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                      placeholder="+32 ..."
                    />
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={handleUpdateInfo}
                      disabled={saving}
                      className="flex-1 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                    >
                      {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingInfo(false);
                        setFormData({
                          first_name: customer?.first_name || '',
                          last_name: customer?.last_name || '',
                          email: customer?.email || '',
                          phone: customer?.phone || '',
                        });
                      }}
                      className="px-6 py-3 border-2 border-gray-300 rounded-lg font-bold hover:bg-gray-50 transition-colors"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Prénom :</span>
                    <span className="font-medium">{customer?.first_name}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Nom :</span>
                    <span className="font-medium">{customer?.last_name}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Email :</span>
                    <span className="font-medium">{customer?.email}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Téléphone :</span>
                    <span className="font-medium">{customer?.phone || 'Non renseigné'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Adresses */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Mes adresses</h2>

              {/* Liste adresses */}
              <div className="space-y-3 mb-6">
                {addresses.map(addr => (
                  <div
                    key={addr.id}
                    className="p-4 border-2 border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium">{addr.street}</div>
                        <div className="text-gray-600">
                          {addr.postal_code} {addr.city}, {addr.country}
                        </div>
                        {addr.is_default && (
                          <span className="inline-block mt-2 px-2 py-1 bg-green-100 text-green-700 text-sm rounded">
                            Par défaut
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteAddress(addr.id)}
                        className="text-red-600 hover:text-red-700 font-medium"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Formulaire nouvelle adresse */}
              <div className="border-t-2 border-gray-200 pt-6">
                <h3 className="text-xl font-bold mb-4">Ajouter une adresse</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Rue et numéro"
                    value={addressForm.street}
                    onChange={e => setAddressForm({ ...addressForm, street: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Code postal"
                      value={addressForm.postal_code}
                      onChange={e => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                      className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                    />
                    <input
                      type="text"
                      placeholder="Ville"
                      value={addressForm.city}
                      onChange={e => setAddressForm({ ...addressForm, city: e.target.value })}
                      className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500"
                    />
                  </div>

                  <button
                    onClick={handleAddAddress}
                    disabled={saving || !addressForm.street || !addressForm.city || !addressForm.postal_code}
                    className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-300 transition-colors"
                  >
                    {saving ? 'Ajout...' : 'Ajouter cette adresse'}
                  </button>
                </div>
              </div>
            </div>

            {/* Actualités par email + emails de commande */}
            <MesAbonnements />

            {/* Notifications push — propres à CET appareil */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Notifications push sur cet appareil</h2>
              <p className="text-sm text-gray-500 mb-4">
                Chaque téléphone, tablette ou ordinateur s’active séparément. Sur iPhone et iPad, la boutique doit être
                installée sur l’écran d’accueil.
              </p>

              {!pushSupported ? (
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Les notifications push ne sont pas supportées par votre navigateur.
                    Pour les activer, installez l'application depuis votre navigateur.
                  </p>
                </div>
              ) : pushPermission === 'denied' ? (
                <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl">
                  <BellOff className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-600">
                    Les notifications sont bloquées. Autorisez-les dans les paramètres de votre navigateur.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Toggle principal */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      {pushSubscribed ? (
                        <Bell className="w-5 h-5 text-green-700" />
                      ) : (
                        <BellOff className="w-5 h-5 text-gray-400" />
                      )}
                      <div>
                        <div className="font-medium text-gray-900">Notifications push</div>
                        <div className="text-sm text-gray-600">
                          Commandes, promotions, actualités
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={async () => {
                        if (pushSubscribed) {
                          await pushUnsubscribe();
                        } else {
                          await pushSubscribe();
                        }
                      }}
                      disabled={pushLoading}
                      className={`relative w-12 h-7 rounded-full transition-colors ${
                        pushSubscribed ? 'bg-green-500' : 'bg-gray-300'
                      } ${pushLoading ? 'opacity-50' : ''}`}
                    >
                      <div
                        className="absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform"
                        style={{
                          transform: pushSubscribed ? 'translateX(20px)' : 'translateX(0)',
                        }}
                      />
                    </button>
                  </div>

                  {/* Types de notifications */}
                  {pushSubscribed && (
                    <div className="pl-4 space-y-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Suivi de commande (confirmation, préparation, prête)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Nouveautés et promotions</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Portefeuille (crédits, remboursements)</span>
                      </div>
                    </div>
                  )}

                  {/* Test notification */}
                  {pushSubscribed && (
                    <button
                      onClick={async () => {
                        try {
                          await sendTestNotification();
                          setTestSent(true);
                          setTimeout(() => setTestSent(false), 3000);
                        } catch {}
                      }}
                      disabled={pushLoading || testSent}
                      className="w-full py-2 px-4 border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                      {testSent ? 'Notification envoyée !' : 'Envoyer une notification test'}
                    </button>
                  )}

                  {pushError && (
                    <p className="text-sm text-red-600">{pushError}</p>
                  )}
                </div>
              )}
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Raccourcis</h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/shop/orders')}
                  className="p-4 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                >
                  <div className="text-3xl mb-2">📦</div>
                  <div className="font-bold">Mes commandes</div>
                </button>
                <button
                  onClick={() => navigate('/shop/wallet')}
                  className="p-4 border-2 border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                >
                  <div className="text-3xl mb-2">💳</div>
                  <div className="font-bold">Mes FOLIES</div>
                </button>
                {/* Les favoris n'ont pas de page a eux : ils ouvrent le
                    CATALOGUE avec leur filtre. Une page separee aurait du
                    redupliquer le stock, les prix, le choix du poids, la
                    precommande et l'ajout au panier — et aurait diverge au
                    premier changement. */}
                <button
                  onClick={() => navigate('/shop?favoris=1')}
                  className="p-4 border-2 border-gray-300 rounded-lg hover:border-[#b45f2e] hover:bg-[#f6e6da] transition-all text-left"
                >
                  <div className="text-3xl mb-2">❤️</div>
                  <div className="font-bold">Mes favoris</div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
